import { ActionErrorDialog } from "@/components/ui/action-error-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { CommentDialog } from "@/components/ui/comment-dialog";
import { toast } from "@/components/ui/sonner";
import {
  OMT_AMS_PORTAL_IMPERSONATE_URL_PARAM,
  REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM,
  UserType,
} from "@/constants";
import {
  approveLicenseModificationRequestMutation,
  createLicenseModificationRequestMutation,
  denyLicenseModificationRequestMutation,
  getLicenseDeregistrationQuestionsOptions,
  updateLicenseAdditionalQuestionsMutation,
  updateLicenseRegistrantMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  ImpersonationRegistrantSearchItemSchema,
  InspectionWithLicenseResponseSchema,
  LicenseCommentsResponseSchema,
  LicensePaymentsResponseSchema,
  LicenseResponseSchema,
  NonRegistrantLicenseCommentsResponseSchema,
  NonRegistrantLicensePaymentsResponseSchema,
  RegistrantQuestionsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useActionErrorDialog } from "@/hooks/use-action-error-dialog";
import { useLicenseHandlers } from "@/hooks/use-license-handlers";
import { approveRegistrationDialogDescription } from "@/lib/approve-registration-dialog";
import { getForeclosureCaseLiveLinkConfig } from "@/lib/attom-foreclosure-case";
import { mergeDeregistrationQuestions } from "@/lib/deregistration-questions";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { omtAmsImpersonateEmailFromAnswer } from "@/lib/omt-ams-impersonate";
import { tagFromQuestionText } from "@/lib/service-transfer-wizard";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, ExternalLink, History, Plus } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { InspectionDetailsSheet } from "../non_registrant/inspection-details-sheet";
import { ActionsCardContent } from "./cards/actions-card";
import { AdditionalQuestionsCardContent } from "./cards/additional-questions-card";
import { AttomForeclosureDetailsDialog } from "./cards/attom-foreclosure-details-dialog";
import { CommentsCardContent } from "./cards/comments-card";
import { DeregistrationAnswersCardContent } from "./cards/deregistration-answers-card";
import { EditAdditionalInfoDialog } from "./cards/edit-additional-info-dialog";
import { EditLicenseRegistrantDialog } from "./cards/edit-license-registrant-dialog";
import { EditSingleAdditionalInfoDialog } from "./cards/edit-single-additional-info-dialog";
import { InspectionsCard } from "./cards/inspections-card";
import { LicenseHistoryDialog } from "./cards/license-history-dialog";
import { LicenseInfoCardContent } from "./cards/license-info-card";
import { LicenseNotificationEmailsCard } from "./cards/license-notification-emails-card";
import {
  RegistrationCurrentPeriodSummaryCard,
  useLicensePaymentsDerived,
} from "./cards/license-payments-derived";
import { LicenseRecordHistoryCard } from "./cards/license-record-history-card";
import { ManualPaymentDialog } from "./cards/manual-payment-dialog";
import {
  type EditableLicensePayment,
  PaymentsCardContent,
} from "./cards/payments-card";
import { PersonCardContent } from "./cards/person-card";
import { PropertyCardContent } from "./cards/property-card";
import { QuestionAnswerHistoryDialog } from "./cards/question-answer-history-dialog";
import { PartyRoleInfo } from "./party-role-info";
import {
  RegistrationDetailsInlineSpinner,
  RegistrationDetailsPaymentsLoading,
} from "./registration-details-section-loading";
import { DetailsTileTitle } from "./tile-info";

const EMPTY_LICENSE_PAYMENTS: LicensePaymentsResponseSchema = { payments: [] };

function OmtAmsImpersonateButton({
  userType,
  questionText,
  answer,
}: {
  userType: UserType;
  questionText: string;
  answer: unknown;
}) {
  if (userType !== UserType.OPERATOR) return null;
  const tag = tagFromQuestionText(questionText);
  if (tag !== "OMT" && tag !== "AMS") return null;
  const email = omtAmsImpersonateEmailFromAnswer(answer);
  if (!email) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      aria-label={`Impersonate ${tag}`}
      onClick={() =>
        window.open(
          `/omt-ams/dashboard?${OMT_AMS_PORTAL_IMPERSONATE_URL_PARAM}=${encodeURIComponent(email)}`,
          "_blank",
        )
      }
    >
      <ExternalLink className="h-4 w-4 mr-2" />
      Impersonate
    </Button>
  );
}

/** Person-card shape for a name/company contact answer; null when nothing was answered. */
function nacAnswerToPersonLike(answer: unknown) {
  let value = answer;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const parsed = value as {
    name?: string;
    companyName?: string;
    company_name?: string;
    email?: string;
    phone?: string;
    addressLine1?: string;
    address1?: string; // Support both field names
    addressLine2?: string;
    address2?: string; // Support both field names
    city?: string;
    state?: string;
    zipCode?: string;
    zipcode?: string; // Support both field names
  };

  const personLike = {
    id: "",
    name: parsed.name ?? null,
    company_name: parsed.companyName ?? parsed.company_name ?? null,
    email: parsed.email ?? null,
    phone: parsed.phone ?? null,
    address1: parsed.addressLine1 ?? parsed.address1 ?? null,
    address2: parsed.addressLine2 ?? parsed.address2 ?? null,
    city: parsed.city ?? null,
    state: parsed.state ?? null,
    zipcode: parsed.zipCode ?? parsed.zipcode ?? null,
  };

  const hasAnyValue = Object.entries(personLike).some(
    ([key, field]) =>
      key !== "id" && typeof field === "string" && field.trim() !== "",
  );
  return hasAnyValue ? personLike : null;
}

function NacAnswerCardActions({
  question,
  rawAnswer,
  hasAnswer,
  userType,
  canEdit,
  onHistory,
  onEdit,
}: {
  question: RegistrantQuestionsResponseSchema["questions"][number];
  rawAnswer: unknown;
  hasAnswer: boolean;
  userType: UserType;
  canEdit: boolean;
  onHistory: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="flex shrink-0 flex-wrap justify-end gap-2">
      <OmtAmsImpersonateButton
        userType={userType}
        questionText={question.question}
        answer={rawAnswer}
      />
      <Button size="sm" variant="outline" onClick={onHistory}>
        <History className="h-4 w-4 mr-2" /> History...
      </Button>
      {canEdit && (
        <Button size="sm" variant="outline" onClick={onEdit}>
          {hasAnswer ? (
            <>
              <Edit className="h-4 w-4 mr-2" /> Edit...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" /> Add...
            </>
          )}
        </Button>
      )}
    </div>
  );
}

function LicenseCardHeaderActions({
  licenseId,
  foreclosureCaseNumber,
  onShowAttomDetails,
  showEdit = false,
  onEdit,
  elevatedOverlays = false,
}: {
  licenseId: string;
  foreclosureCaseNumber: string | null;
  onShowAttomDetails: () => void;
  showEdit?: boolean;
  onEdit?: () => void;
  elevatedOverlays?: boolean;
}) {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  if (!foreclosureCaseNumber && !showEdit) {
    return null;
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 justify-end">
        {foreclosureCaseNumber ? (
          <Button size="sm" variant="outline" onClick={onShowAttomDetails}>
            Show ATTOM Details...
          </Button>
        ) : null}
        {showEdit ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsHistoryOpen(true)}
          >
            <History className="h-4 w-4 mr-2" /> History...
          </Button>
        ) : null}
        {showEdit && onEdit ? (
          <Button
            size="sm"
            variant="outline"
            className="min-w-[80px]"
            onClick={onEdit}
          >
            <Edit className="h-4 w-4 mr-2" /> Edit
          </Button>
        ) : null}
      </div>
      {showEdit ? (
        <LicenseHistoryDialog
          licenseId={licenseId}
          open={isHistoryOpen}
          onOpenChange={setIsHistoryOpen}
          elevated={elevatedOverlays}
        />
      ) : null}
    </>
  );
}

interface LicenseDetailsProps {
  data: LicenseResponseSchema;
  commentsData:
    | NonRegistrantLicenseCommentsResponseSchema
    | LicenseCommentsResponseSchema;
  paymentsData:
    | NonRegistrantLicensePaymentsResponseSchema
    | LicensePaymentsResponseSchema;
  additionalQuestions?: RegistrantQuestionsResponseSchema;
  userType: UserType;
  /** Section still fetching (show spinner inside card instead of blocking the page). */
  paymentsLoading?: boolean;
  commentsLoading?: boolean;
  additionalQuestionsLoading?: boolean;
  /** Background full runtime refresh updating license totals on the server. */
  isLicenseRuntimeRefreshing?: boolean;
  /** After payment (or cancel), scroll the Property card into view once (registrant portal). */
  initialFocusSection?: "property" | null;
  onInitialFocusSectionConsumed?: () => void;
  onCommentAdded?: (comment: string, isPublic: boolean) => Promise<void>;
  onRegistrantComment?: (input: {
    comment: string;
    notifyRegistry: boolean;
    markIssueResolved: boolean;
    files: File[];
  }) => Promise<void>;
  onFlagComplianceIssue?: (input: {
    comment: string;
    files: File[];
  }) => Promise<void>;
  onClearRegistryNotification?: (commentId: string) => Promise<void>;
  onClearComplianceIssue?: () => Promise<void>;
  onCommentDeleted?: (commentId: string) => void;
  /**
   * When license details are shown inside an elevated dialog (e.g. from case
   * review), nest child dialogs/sheets at the elevated z-index so Edit and
   * other controls stay clickable in front of the parent modal.
   */
  elevatedOverlays?: boolean;
}

export function LicenseDetails({
  userType,
  data,
  commentsData,
  paymentsData,
  additionalQuestions,
  paymentsLoading = false,
  commentsLoading = false,
  additionalQuestionsLoading = false,
  isLicenseRuntimeRefreshing = false,
  initialFocusSection = null,
  onInitialFocusSectionConsumed,
  onCommentAdded,
  onRegistrantComment,
  onFlagComplianceIssue,
  onClearRegistryNotification,
  onClearComplianceIssue,
  onCommentDeleted,
  elevatedOverlays = false,
}: LicenseDetailsProps) {
  const {
    handleApproveLicense,
    handleRejectLicense,
    showApproveDialog,
    showRejectDialog,
    isApproveDialogOpen,
    setIsApproveDialogOpen,
    isRejectDialogOpen,
    setIsRejectDialogOpen,
    isApproveLoading,
    isRejectLoading,
  } = useLicenseHandlers();

  const actionErrorDialog = useActionErrorDialog();

  const registrant = data.registrant_account;
  const property = data.property;
  const jurisdiction = data.jurisdiction;

  const [isManualPaymentDialogOpen, setIsManualPaymentDialogOpen] =
    useState(false);
  const [manualPaymentDialogVariant, setManualPaymentDialogVariant] = useState<
    "payment" | "fee_adjustment"
  >("payment");
  const [editingPayment, setEditingPayment] =
    useState<EditableLicensePayment | null>(null);
  const [selectedInspectionId, setSelectedInspectionId] = useState<
    string | null
  >(null);
  const [isInspectionSheetOpen, setIsInspectionSheetOpen] = useState(false);

  // Memoize selected inspection (must be at top level, not conditional)
  const selectedInspection = useMemo(() => {
    if (!selectedInspectionId || !data.inspections) return null;
    const inspection = data.inspections.find(
      (i) => i.id === selectedInspectionId,
    );
    if (!inspection) return null;
    // Convert InspectionResponseSchema to InspectionWithLicenseResponseSchema
    return {
      ...inspection,
      license_id: data.id,
      license_display_id: data.display_id,
      license_type_name: data.license_type?.name ?? "",
      jurisdiction_id: data.jurisdiction?.id ?? "",
      jurisdiction_name: data.jurisdiction?.name ?? "",
      property_address: data.property?.display_address ?? "",
    } as InspectionWithLicenseResponseSchema;
  }, [selectedInspectionId, data]);

  const showDeregistrationAnswers = data.effective_end_date != null;
  const { data: deregistrationQuestionsData } = useQuery({
    ...getLicenseDeregistrationQuestionsOptions({
      path: { license_id: data.id },
      query:
        userType !== UserType.REGISTRANT
          ? { include_deleted: true }
          : undefined,
    }),
    enabled: showDeregistrationAnswers,
  });
  const deregistrationQuestions = useMemo(
    () =>
      mergeDeregistrationQuestions(
        additionalQuestions?.questions,
        deregistrationQuestionsData?.questions,
      ),
    [additionalQuestions?.questions, deregistrationQuestionsData?.questions],
  );

  const [isEditAdditionalInfoDialogOpen, setIsEditAdditionalInfoDialogOpen] =
    useState(false);
  const [isEditRegistrantDialogOpen, setIsEditRegistrantDialogOpen] =
    useState(false);
  const [forceLicenseEdit, setForceLicenseEdit] = useState(false);
  const [forcePropertyEditMobile, setForcePropertyEditMobile] = useState(false);
  const [forcePropertyEditDesktop, setForcePropertyEditDesktop] =
    useState(false);
  const [isEditSingleDialogOpen, setIsEditSingleDialogOpen] = useState(false);
  const [isAttomForeclosureDialogOpen, setIsAttomForeclosureDialogOpen] =
    useState(false);
  const [questionToEdit, setQuestionToEdit] = useState<
    RegistrantQuestionsResponseSchema["questions"][number] | null
  >(null);
  const [questionHistoryScope, setQuestionHistoryScope] = useState<{
    title: string;
    questionIds: string[];
  } | null>(null);

  const additionalInfoQuestionIds = useMemo(
    () =>
      additionalQuestions?.questions
        .filter(
          (question) =>
            question.question_type === "registration" &&
            !isNacStyleAnswerType(question.answer_type),
        )
        .map((question) => question.id) ?? [],
    [additionalQuestions?.questions],
  );

  const foreclosureCaseLiveLink = useMemo(
    () =>
      getForeclosureCaseLiveLinkConfig(
        additionalQuestions?.questions,
        data.answers_to_additional_questions,
      ),
    [additionalQuestions?.questions, data.answers_to_additional_questions],
  );
  const foreclosureCaseNumber = foreclosureCaseLiveLink.caseNumber;

  const propertyMobileScrollRef = useRef<HTMLDivElement | null>(null);
  const propertyDesktopScrollRef = useRef<HTMLDivElement | null>(null);
  const propertyFocusDoneRef = useRef(false);

  useLayoutEffect(() => {
    if (initialFocusSection !== "property") {
      propertyFocusDoneRef.current = false;
      return;
    }
    if (userType !== UserType.REGISTRANT || !property) {
      return;
    }
    if (propertyFocusDoneRef.current) {
      return;
    }
    const mdUp = window.matchMedia("(min-width: 768px)").matches;
    const el = mdUp
      ? propertyDesktopScrollRef.current
      : propertyMobileScrollRef.current;
    if (!el) {
      propertyFocusDoneRef.current = true;
      onInitialFocusSectionConsumed?.();
      return;
    }
    propertyFocusDoneRef.current = true;
    const run = () => {
      el.scrollIntoView({ block: "start", behavior: "smooth" });
      onInitialFocusSectionConsumed?.();
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }, [initialFocusSection, userType, property, onInitialFocusSectionConsumed]);

  const handleEditPayment = (payment: EditableLicensePayment) => {
    setEditingPayment(payment);
    setManualPaymentDialogVariant(
      payment.manual_payment_method_type === "fee_adjustment"
        ? "fee_adjustment"
        : "payment",
    );
    setIsManualPaymentDialogOpen(true);
  };

  const handleManualPaymentDialogChange = (open: boolean) => {
    setIsManualPaymentDialogOpen(open);
    if (!open) {
      setEditingPayment(null);
    }
  };

  const queryClient = useQueryClient();

  const handleLicenseDataChanged = useCallback(
    () => notifyLicenseDetailsChanged(queryClient, data.id),
    [data.id, queryClient],
  );

  const updateAdditionalInfoMutation = useMutation({
    ...updateLicenseAdditionalQuestionsMutation(),
  });
  const updateRegistrantMutation = useMutation({
    ...updateLicenseRegistrantMutation(),
  });
  const createModificationRequestMutation = useMutation({
    ...createLicenseModificationRequestMutation(),
  });
  const approveModificationRequestMutation = useMutation({
    ...approveLicenseModificationRequestMutation(),
  });
  const denyModificationRequestMutation = useMutation({
    ...denyLicenseModificationRequestMutation(),
  });

  const paymentsForDerived = paymentsData ?? EMPTY_LICENSE_PAYMENTS;

  const paymentsDerived = useLicensePaymentsDerived({
    payments: paymentsForDerived,
    userType,
    licenseId: data.id,
    licenseData: data,
  });

  const handleUpdateAdditionalInfo = async (answers: Record<string, any>) => {
    try {
      const modificationRequired =
        userType === UserType.REGISTRANT &&
        data.license_modification_requests_enabled === true &&
        data.status === "COMPLIANT_UNTIL_RENEWAL_DATE" &&
        data.approved_at != null;
      if (modificationRequired) {
        if (data.pending_modification_request) {
          toast({
            title: "A modification is already open",
            description:
              "This registration already has a change waiting for payment or review.",
            variant: "destructive",
          });
          return;
        }
        const feeText =
          data.modification_fee != null && data.modification_fee !== ""
            ? ` The fee is $${data.modification_fee}.`
            : "";
        const confirmed = window.confirm(
          `This registration is already approved. Submitting these changes starts a license modification. The current certificate stays in effect until the change is approved.${feeText}`,
        );
        if (!confirmed) return;
        const result = await createModificationRequestMutation.mutateAsync({
          path: { license_id: data.id },
          body: {
            proposed_answers: answers,
            payment_success_url: `${window.location.origin}/registrant/payment-success?sessionId={CHECKOUT_SESSION_ID}`,
            payment_cancel_url: `${window.location.origin}/registrant/licenses/${data.id}`,
            payment_method_type: "card",
          },
        });
        if (result?.checkout_url) {
          window.location.href = result.checkout_url;
          return;
        }
        await notifyLicenseDetailsChanged(queryClient, data.id);
        toast({
          title: "Modification submitted",
          description:
            "The current certificate stays in effect until the change is approved.",
          variant: "secondary",
        });
        setIsEditAdditionalInfoDialogOpen(false);
        return;
      }

      // Convert answers to the format expected by the API
      const answersArray = Object.entries(answers).map(
        ([questionId, answer]) => ({
          question_id: questionId,
          answer: answer,
        }),
      );

      await updateAdditionalInfoMutation.mutateAsync({
        path: { license_id: data.id },
        body: {
          answers_to_additional_questions: answersArray,
        },
      });

      await notifyLicenseDetailsChanged(queryClient, data.id);

      toast({
        title: "Success",
        description: "Additional information updated successfully",
        variant: "secondary",
      });

      setIsEditAdditionalInfoDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to update additional information: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  };

  const handleUpdateRegistrant = async (
    selectedRegistrant: ImpersonationRegistrantSearchItemSchema,
  ) => {
    try {
      await updateRegistrantMutation.mutateAsync({
        path: { license_id: data.id },
        body: { registrant_account_id: selectedRegistrant.id },
      });
      await notifyLicenseDetailsChanged(queryClient, data.id);
      toast({
        title: "Registrant updated",
        description: `This license is now assigned to ${selectedRegistrant.email}.`,
        variant: "secondary",
      });
      setIsEditRegistrantDialogOpen(false);
    } catch (error) {
      toast({
        title: "Unable to update registrant",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const renderPropertyCard = (placement: "mobile" | "desktop") => {
    if (!property) return null;
    const canEditProperty =
      userType === UserType.OPERATOR || userType === UserType.GOVERNMENT;
    const forceEdit =
      placement === "mobile"
        ? forcePropertyEditMobile
        : forcePropertyEditDesktop;
    return (
      <div
        ref={
          userType === UserType.REGISTRANT
            ? placement === "mobile"
              ? propertyMobileScrollRef
              : propertyDesktopScrollRef
            : undefined
        }
        className={userType === UserType.REGISTRANT ? "scroll-mt-4" : undefined}
      >
        <Card className={placement === "mobile" ? "gap-4 @md:hidden" : "gap-4"}>
          <CardHeader className="flex flex-row items-start justify-between">
            <DetailsTileTitle name="Property" />
            {canEditProperty ? (
              <Button
                size="sm"
                variant="outline"
                className="min-w-[80px]"
                onClick={() => {
                  if (placement === "mobile") {
                    setForcePropertyEditMobile((prev) => !prev);
                    return;
                  }
                  setForcePropertyEditDesktop((prev) => !prev);
                }}
              >
                <Edit className="h-4 w-4 mr-2" /> Edit
              </Button>
            ) : null}
          </CardHeader>
          <PropertyCardContent
            property={property}
            jurisdiction={jurisdiction}
            userType={userType}
            licenseId={data.id}
            forceEdit={forceEdit}
          />
        </Card>
      </div>
    );
  };

  const renderActionsCard = (placement: "mobile" | "desktop") => {
    if (userType === UserType.OMT_AMS) return null;
    return (
      <Card className={placement === "mobile" ? "gap-4 @md:hidden" : "gap-4"}>
        <CardHeader>
          <DetailsTileTitle name="Actions" />
        </CardHeader>
        <ActionsCardContent
          data={data}
          userType={userType}
          showApproveDialog={showApproveDialog}
          showRejectDialog={showRejectDialog}
          deregistrationQuestions={deregistrationQuestions}
        />
      </Card>
    );
  };

  const renderCommentsCard = () => (
    <Card className="gap-4">
      <CardHeader>
        <DetailsTileTitle name="Issues & Comments" />
      </CardHeader>
      {commentsLoading ? (
        <RegistrationDetailsInlineSpinner label="Loading comments" />
      ) : (
        <CommentsCardContent
          commentsData={commentsData}
          userType={userType}
          onCommentAdded={onCommentAdded}
          onRegistrantComment={onRegistrantComment}
          onFlagComplianceIssue={onFlagComplianceIssue}
          onClearRegistryNotification={onClearRegistryNotification}
          onClearComplianceIssue={onClearComplianceIssue}
          onCommentDeleted={onCommentDeleted}
        />
      )}
    </Card>
  );

  const pendingModification = data.pending_modification_request;
  const reviewModification = async (decision: "approvals" | "denials") => {
    if (!pendingModification?.id) return;
    const reason =
      decision === "denials"
        ? window.prompt("Reason for denying this change")
        : null;
    if (decision === "denials" && reason === null) return;
    try {
      const mutation =
        decision === "approvals"
          ? approveModificationRequestMutation
          : denyModificationRequestMutation;
      await mutation.mutateAsync({
        path: { request_id: pendingModification.id },
        body: { reason },
      });
      await notifyLicenseDetailsChanged(queryClient, data.id);
      toast({
        title: decision === "approvals" ? "Change approved" : "Change denied",
        description:
          decision === "denials"
            ? "The current registration certificate is still valid."
            : "The certificate will show the approved details.",
        variant: "secondary",
      });
    } catch (error) {
      toast({
        title: "Unable to review the change",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full space-y-6 @container">
      {pendingModification ? (
        <Card className="gap-4 border-amber-300 bg-amber-50">
          <CardHeader className="space-y-3">
            <CardTitle>Pending license modification</CardTitle>
            <p className="text-sm">
              A change is {pendingModification.status?.replaceAll("_", " ")}.
              The current certificate still shows the approved details.
            </p>
            {userType !== UserType.REGISTRANT &&
            pendingModification.status === "awaiting_review" ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => reviewModification("approvals")}
                >
                  Approve change
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => reviewModification("denials")}
                >
                  Deny change
                </Button>
              </div>
            ) : null}
          </CardHeader>
        </Card>
      ) : null}
      {/* This container handles the single-column layout for narrow containers */}
      <div className="@md:hidden space-y-6">
        {renderPropertyCard("mobile")}
        {renderActionsCard("mobile")}
        {renderCommentsCard()}
        <Card className="gap-4">
          <CardHeader className="flex flex-row items-start justify-between">
            <DetailsTileTitle name="License" />
            <LicenseCardHeaderActions
              licenseId={data.id}
              foreclosureCaseNumber={foreclosureCaseNumber}
              onShowAttomDetails={() => setIsAttomForeclosureDialogOpen(true)}
              showEdit={
                userType === UserType.OPERATOR ||
                userType === UserType.GOVERNMENT
              }
              onEdit={() => setForceLicenseEdit((prev) => !prev)}
              elevatedOverlays={elevatedOverlays}
            />
          </CardHeader>
          <LicenseInfoCardContent
            data={data}
            userType={userType}
            licenseId={data.id}
            forceEdit={forceLicenseEdit}
            showRegistrationCertificateButton={!property}
          />
        </Card>
        <Card className="gap-4 @md:hidden">
          <CardHeader className="flex flex-row items-start justify-between">
            <DetailsTileTitle name="Registrant" />
            {userType === UserType.REGISTRANT && (
              <Button
                asChild
                size="sm"
                variant="outline"
                className="min-w-[80px]"
              >
                <a href="/registrant/account">
                  <Edit className="h-4 w-4 mr-2 inline" /> Edit
                </a>
              </Button>
            )}
            {userType === UserType.OPERATOR && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditRegistrantDialogOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit...
                </Button>
                {registrant ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(
                        `/registrant/dashboard?${REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM}=${registrant.id}`,
                        "_blank",
                      )
                    }
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Impersonate
                  </Button>
                ) : null}
              </div>
            )}
          </CardHeader>
          <PersonCardContent
            person={registrant}
            userType={userType}
            licenseId={data.id}
          />
        </Card>
        {/* Render a tile for each name/contact/address question (registration only) under Registrant (mobile) */}
        {additionalQuestions?.questions
          .filter(
            (q) =>
              q.question_type === "registration" &&
              isNacStyleAnswerType(q.answer_type),
          )
          .map((q) => {
            const rawAnswer = data.answers_to_additional_questions[q.id];
            const personLike = nacAnswerToPersonLike(rawAnswer);
            const canEdit =
              userType === UserType.OPERATOR ||
              userType === UserType.REGISTRANT ||
              userType === UserType.GOVERNMENT;
            // Unanswered questions still get a tile so the answer can be added.
            if (!personLike && !canEdit) return null;

            return (
              <Card key={q.id} className="gap-4 @md:hidden">
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                  <CardTitle className="flex min-w-0 flex-1 items-center gap-1 font-semibold text-xl">
                    <span className="min-w-0 break-words">{q.question}</span>
                    <PartyRoleInfo questionText={q.question} />
                  </CardTitle>
                  <NacAnswerCardActions
                    question={q}
                    rawAnswer={rawAnswer}
                    hasAnswer={personLike !== null}
                    userType={userType}
                    canEdit={canEdit}
                    onHistory={() =>
                      setQuestionHistoryScope({
                        title: `${q.question} history`,
                        questionIds: [q.id],
                      })
                    }
                    onEdit={() => {
                      setQuestionToEdit(q);
                      setIsEditSingleDialogOpen(true);
                    }}
                  />
                </CardHeader>
                <PersonCardContent
                  person={personLike as any}
                  userType={userType}
                  licenseId={data.id}
                  useContactLabel={
                    q.answer_type === "company_contact" &&
                    Boolean(q.use_contact_label)
                  }
                />
              </Card>
            );
          })}

        <Card className="gap-4 @md:hidden">
          <CardHeader className="flex flex-row items-start justify-between">
            <DetailsTileTitle name="Additional Information" />
            {!additionalQuestionsLoading && (
              <div className="flex flex-wrap justify-end gap-2">
                {additionalInfoQuestionIds.length > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setQuestionHistoryScope({
                        title: "Additional information history",
                        questionIds: additionalInfoQuestionIds,
                      })
                    }
                  >
                    <History className="h-4 w-4 mr-2" />
                    History...
                  </Button>
                ) : null}
                {(userType === UserType.OPERATOR ||
                  userType === UserType.GOVERNMENT ||
                  userType === UserType.REGISTRANT) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-w-[80px]"
                    onClick={() => setIsEditAdditionalInfoDialogOpen(true)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit...
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          {additionalQuestionsLoading ? (
            <RegistrationDetailsInlineSpinner label="Loading additional information" />
          ) : (
            <AdditionalQuestionsCardContent
              additionalQuestions={additionalQuestions}
              data={data}
            />
          )}
        </Card>
        {showDeregistrationAnswers && deregistrationQuestions.length > 0 && (
          <Card className="gap-4 @md:hidden">
            <CardHeader>
              <DetailsTileTitle name="De-registration answers" />
            </CardHeader>
            <DeregistrationAnswersCardContent
              questions={deregistrationQuestions}
              data={data}
            />
          </Card>
        )}
        {(userType === UserType.OPERATOR ||
          userType === UserType.GOVERNMENT) && (
          <InspectionsCard
            data={data}
            userType={userType}
            questions={additionalQuestions?.questions}
            onInspectionClick={(inspectionId) => {
              setSelectedInspectionId(inspectionId);
              setIsInspectionSheetOpen(true);
            }}
            onInspectorUpdated={handleLicenseDataChanged}
          />
        )}
        {paymentsLoading ? (
          <RegistrationDetailsPaymentsLoading userType={userType} />
        ) : (
          <>
            <RegistrationCurrentPeriodSummaryCard
              derived={paymentsDerived}
              isRefreshing={isLicenseRuntimeRefreshing}
              userType={userType}
            />
            <Card className="gap-4 @md:hidden">
              <CardHeader className="flex flex-row items-center justify-between">
                <DetailsTileTitle name="Payments" />
                {userType === UserType.OPERATOR && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setManualPaymentDialogVariant("payment");
                        setEditingPayment(null);
                        setIsManualPaymentDialogOpen(true);
                      }}
                      size="sm"
                      className="min-w-[80px]"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Payment...
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setManualPaymentDialogVariant("fee_adjustment");
                        setEditingPayment(null);
                        setIsManualPaymentDialogOpen(true);
                      }}
                      size="sm"
                      className="min-w-[80px]"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Fee Adjustment...
                    </Button>
                  </div>
                )}
              </CardHeader>
              <PaymentsCardContent
                payments={paymentsData ?? EMPTY_LICENSE_PAYMENTS}
                userType={userType}
                licenseId={data.id}
                licenseData={data}
                onPaymentEdit={handleEditPayment}
                derived={paymentsDerived}
                elevatedOverlays={elevatedOverlays}
              />
            </Card>
            {userType === UserType.OPERATOR ? (
              <LicenseRecordHistoryCard data={data} />
            ) : null}
            {userType === UserType.OPERATOR ||
            userType === UserType.GOVERNMENT ? (
              <LicenseNotificationEmailsCard licenseId={data.id} />
            ) : null}
          </>
        )}
      </div>

      {/* This container handles the two-column layout for wider containers */}
      <div className="hidden @md:grid @lg:grid-cols-3 gap-6">
        {/* --- MAIN CONTENT COLUMN --- */}
        <div className="@md:col-span-2 gap-6 space-y-6">
          {renderPropertyCard("desktop")}
          {renderActionsCard("desktop")}
          {renderCommentsCard()}
          {userType === UserType.REGISTRANT && (
            <InspectionsCard
              data={data}
              userType={userType}
              questions={additionalQuestions?.questions}
              onInspectionClick={(inspectionId) => {
                setSelectedInspectionId(inspectionId);
                setIsInspectionSheetOpen(true);
              }}
              onInspectorUpdated={handleLicenseDataChanged}
            />
          )}
          <Card className="gap-4">
            <CardHeader className="flex flex-row items-start justify-between">
              <DetailsTileTitle name="Additional Information" />
              {!additionalQuestionsLoading && (
                <div className="flex flex-wrap justify-end gap-2">
                  {additionalInfoQuestionIds.length > 0 ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setQuestionHistoryScope({
                          title: "Additional information history",
                          questionIds: additionalInfoQuestionIds,
                        })
                      }
                    >
                      <History className="h-4 w-4 mr-2" />
                      History...
                    </Button>
                  ) : null}
                  {(userType === UserType.OPERATOR ||
                    userType === UserType.GOVERNMENT ||
                    userType === UserType.REGISTRANT) && (
                    <Button
                      onClick={() => setIsEditAdditionalInfoDialogOpen(true)}
                      size="sm"
                      variant="outline"
                      className="min-w-[80px]"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit...
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            {additionalQuestionsLoading ? (
              <RegistrationDetailsInlineSpinner label="Loading additional information" />
            ) : (
              <AdditionalQuestionsCardContent
                additionalQuestions={additionalQuestions}
                data={data}
              />
            )}
          </Card>
          {showDeregistrationAnswers && deregistrationQuestions.length > 0 && (
            <Card className="gap-4">
              <CardHeader>
                <DetailsTileTitle name="De-registration answers" />
              </CardHeader>
              <DeregistrationAnswersCardContent
                questions={deregistrationQuestions}
                data={data}
              />
            </Card>
          )}
          {(userType === UserType.OPERATOR ||
            userType === UserType.GOVERNMENT) && (
            <InspectionsCard
              data={data}
              userType={userType}
              questions={additionalQuestions?.questions}
              onInspectionClick={(inspectionId) => {
                setSelectedInspectionId(inspectionId);
                setIsInspectionSheetOpen(true);
              }}
              onInspectorUpdated={handleLicenseDataChanged}
            />
          )}
        </div>

        {/* --- SIDEBAR COLUMN --- */}
        <div className="@md:col-start-3 gap-6 space-y-6">
          <Card className="gap-4">
            <CardHeader className="flex flex-row items-start justify-between">
              <DetailsTileTitle name="License" />
              <LicenseCardHeaderActions
                licenseId={data.id}
                foreclosureCaseNumber={foreclosureCaseNumber}
                onShowAttomDetails={() => setIsAttomForeclosureDialogOpen(true)}
                showEdit={
                  userType === UserType.OPERATOR ||
                  userType === UserType.GOVERNMENT
                }
                onEdit={() => setForceLicenseEdit((prev) => !prev)}
                elevatedOverlays={elevatedOverlays}
              />
            </CardHeader>
            <LicenseInfoCardContent
              data={data}
              userType={userType}
              licenseId={data.id}
              forceEdit={forceLicenseEdit}
              showRegistrationCertificateButton={!property}
            />
          </Card>
          <Card className="gap-4">
            <CardHeader className="flex flex-row items-start justify-between">
              <DetailsTileTitle name="Registrant" />
              {userType === UserType.REGISTRANT && (
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="min-w-[80px]"
                >
                  <a href="/registrant/account">
                    <Edit className="h-4 w-4 mr-2 inline" /> Edit
                  </a>
                </Button>
              )}
              {userType === UserType.OPERATOR && (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditRegistrantDialogOpen(true)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit...
                  </Button>
                  {registrant ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        window.open(
                          `/registrant/dashboard?${REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM}=${registrant.id}`,
                          "_blank",
                        )
                      }
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Impersonate
                    </Button>
                  ) : null}
                </div>
              )}
            </CardHeader>
            <PersonCardContent
              person={registrant}
              userType={userType}
              licenseId={data.id}
            />
          </Card>

          {/* Render a tile for each name/contact/address question (registration only) */}
          {additionalQuestions?.questions
            .filter(
              (q) =>
                q.question_type === "registration" &&
                isNacStyleAnswerType(q.answer_type),
            )
            .map((q) => {
              const rawAnswer = data.answers_to_additional_questions[q.id];
              const personLike = nacAnswerToPersonLike(rawAnswer);
              const canEdit =
                userType === UserType.OPERATOR ||
                userType === UserType.REGISTRANT ||
                userType === UserType.GOVERNMENT;
              // Unanswered questions still get a tile so the answer can be added.
              if (!personLike && !canEdit) return null;

              return (
                <Card key={q.id} className="gap-4">
                  <CardHeader className="flex flex-row items-start justify-between gap-2">
                    <CardTitle className="flex min-w-0 flex-1 items-center gap-1 font-semibold text-xl">
                      <span className="min-w-0 break-words">{q.question}</span>
                      <PartyRoleInfo questionText={q.question} />
                    </CardTitle>
                    <NacAnswerCardActions
                      question={q}
                      rawAnswer={rawAnswer}
                      hasAnswer={personLike !== null}
                      userType={userType}
                      canEdit={canEdit}
                      onHistory={() =>
                        setQuestionHistoryScope({
                          title: `${q.question} history`,
                          questionIds: [q.id],
                        })
                      }
                      onEdit={() => {
                        setQuestionToEdit(q);
                        setIsEditSingleDialogOpen(true);
                      }}
                    />
                  </CardHeader>
                  <PersonCardContent
                    person={personLike as any}
                    userType={userType}
                    licenseId={data.id}
                    useContactLabel={
                      q.answer_type === "company_contact" &&
                      Boolean(q.use_contact_label)
                    }
                  />
                </Card>
              );
            })}

          {paymentsLoading ? (
            <RegistrationDetailsPaymentsLoading userType={userType} />
          ) : (
            <>
              <RegistrationCurrentPeriodSummaryCard
                derived={paymentsDerived}
                isRefreshing={isLicenseRuntimeRefreshing}
                userType={userType}
              />
              <Card className="gap-4">
                <CardHeader className="flex flex-row items-start justify-between">
                  <DetailsTileTitle name="Payments" />
                  {userType === UserType.OPERATOR && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setManualPaymentDialogVariant("payment");
                          setEditingPayment(null);
                          setIsManualPaymentDialogOpen(true);
                        }}
                        size="sm"
                        className="min-w-[80px]"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Payment...
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setManualPaymentDialogVariant("fee_adjustment");
                          setEditingPayment(null);
                          setIsManualPaymentDialogOpen(true);
                        }}
                        size="sm"
                        className="min-w-[80px]"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Fee Adjustment...
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <PaymentsCardContent
                  payments={paymentsData ?? EMPTY_LICENSE_PAYMENTS}
                  userType={userType}
                  licenseId={data.id}
                  licenseData={data}
                  onPaymentEdit={handleEditPayment}
                  derived={paymentsDerived}
                  elevatedOverlays={elevatedOverlays}
                />
              </Card>
              {userType === UserType.OPERATOR ? (
                <LicenseRecordHistoryCard data={data} />
              ) : null}
              {userType === UserType.OPERATOR ||
              userType === UserType.GOVERNMENT ? (
                <LicenseNotificationEmailsCard licenseId={data.id} />
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Manual Payment Dialog - Operators only */}
      {userType === UserType.OPERATOR && (
        <ManualPaymentDialog
          isOpen={isManualPaymentDialogOpen}
          onOpenChange={handleManualPaymentDialogChange}
          licenseId={data.id}
          variant={manualPaymentDialogVariant}
          editPayment={editingPayment}
          elevated={elevatedOverlays}
          onPaymentAdded={() => {
            // Refresh payments data if needed
          }}
        />
      )}

      {/* Comment Dialogs */}
      <CommentDialog
        isOpen={isApproveDialogOpen}
        onOpenChange={setIsApproveDialogOpen}
        title="Approve Registration"
        description={approveRegistrationDialogDescription(data.status, data)}
        actionLabel="Approve"
        onConfirm={handleApproveLicense}
        isLoading={isApproveLoading}
        elevated={elevatedOverlays}
      />

      <CommentDialog
        isOpen={isRejectDialogOpen}
        onOpenChange={setIsRejectDialogOpen}
        title="Reject Registration"
        description="Are you sure you want to reject this registration/renewal? You can add an optional note explaining your decision."
        actionLabel="Reject"
        onConfirm={handleRejectLicense}
        isLoading={isRejectLoading}
        elevated={elevatedOverlays}
      />

      <ActionErrorDialog
        isOpen={actionErrorDialog.isOpen}
        onOpenChange={actionErrorDialog.onOpenChange}
        title={actionErrorDialog.title}
        description={actionErrorDialog.description}
        elevated={elevatedOverlays}
      />

      {userType === UserType.OPERATOR ? (
        <EditLicenseRegistrantDialog
          open={isEditRegistrantDialogOpen}
          onOpenChange={setIsEditRegistrantDialogOpen}
          currentRegistrantId={registrant?.id}
          onSave={handleUpdateRegistrant}
          isSaving={updateRegistrantMutation.isPending}
          elevated={elevatedOverlays}
        />
      ) : null}

      {foreclosureCaseNumber ? (
        <AttomForeclosureDetailsDialog
          open={isAttomForeclosureDialogOpen}
          onOpenChange={setIsAttomForeclosureDialogOpen}
          caseNumber={foreclosureCaseNumber}
          jurisdictionFips={jurisdiction?.fips_code}
          caseNumberUrlTemplate={foreclosureCaseLiveLink.urlTemplate}
          caseNumberCopyToClipboard={foreclosureCaseLiveLink.copyToClipboard}
          caseNumberHttpMethod={foreclosureCaseLiveLink.httpMethod}
          caseNumberValueExtractRegex={
            foreclosureCaseLiveLink.valueExtractRegex
          }
          caseNumberRequestFields={foreclosureCaseLiveLink.requestFields}
          caseNumberBrowserHelperConfig={
            foreclosureCaseLiveLink.browserHelperConfig
          }
          caseNumberBlankAnswerUrl={foreclosureCaseLiveLink.blankAnswerUrl}
          elevated={elevatedOverlays}
        />
      ) : null}

      {/* Edit Additional Information Dialog */}
      <EditAdditionalInfoDialog
        isOpen={isEditAdditionalInfoDialogOpen}
        onOpenChange={setIsEditAdditionalInfoDialogOpen}
        data={data}
        additionalQuestions={additionalQuestions}
        onSave={handleUpdateAdditionalInfo}
        userType={userType}
        elevated={elevatedOverlays}
      />

      {/* Edit Single Additional Info Dialog */}
      <EditSingleAdditionalInfoDialog
        isOpen={isEditSingleDialogOpen}
        onOpenChange={setIsEditSingleDialogOpen}
        data={data}
        question={questionToEdit}
        questions={additionalQuestions?.questions}
        userType={userType}
        elevated={elevatedOverlays}
        onSave={async (partial) => {
          // Merge with existing answers similar to bulk dialog
          const updated = {
            ...data.answers_to_additional_questions,
            ...partial,
          } as Record<string, any>;
          await handleUpdateAdditionalInfo(updated);
        }}
      />

      {questionHistoryScope ? (
        <QuestionAnswerHistoryDialog
          licenseId={data.id}
          questionIds={questionHistoryScope.questionIds}
          title={questionHistoryScope.title}
          open
          onOpenChange={(open) => {
            if (!open) setQuestionHistoryScope(null);
          }}
          elevated={elevatedOverlays}
        />
      ) : null}

      {/* Inspection Details Sheet */}
      {selectedInspectionId && (
        <InspectionDetailsSheet
          isOpen={isInspectionSheetOpen}
          onOpenChange={setIsInspectionSheetOpen}
          inspection={selectedInspection}
          userType={userType}
          elevated={elevatedOverlays}
          onInspectionUpdated={() => {
            // Refetch license data to get updated inspection info
            queryClient.invalidateQueries({
              queryKey: ["licenseDetails"],
            });
            void handleLicenseDataChanged();
          }}
        />
      )}
    </div>
  );
}
